import React from 'react';
import { Badge, Box, Flex, Text } from '@chakra-ui/react';
import AdminState from './AdminState';
import { CheckIcon, CloseIcon, InfoIcon } from './AdminIcon';

function reasonKey(reason) {
  return JSON.stringify([reason.type, reason.message, reason.requiredBadgeEntityIds || []]);
}

function requirementMessage(reason, badgeNames) {
  if (reason.type === 'NO_BADGE' && reason.requiredBadgeEntityIds?.length) {
    const names = reason.requiredBadgeEntityIds.map(id => badgeNames[id] || id);
    return names.length === 1 ? `Required badge: ${names[0]}` : `Requires one of these badges: ${names.join(', ')}`;
  }
  return reason.message;
}

/** Shared account/space blockers belong above the list; equipment-specific requirements stay on their row. */
export default function ToolAccessList({ instances, labId, badgeNames = {} }) {
  if (!instances.length) return <AdminState title="No tool instances in this space"
    description="Tool access will appear here once equipment has been added to this space." />;

  const orderedInstances = [...instances].sort((a, b) =>
    Number(Boolean(b.canAccess)) - Number(Boolean(a.canAccess)) ||
    (a.machineName || '').localeCompare(b.machineName || '', undefined, { numeric: true, sensitivity: 'base' }));
  const unavailable = instances.filter(instance => !instance.canAccess);
  const shared = unavailable.length > 1 ? (unavailable[0].reasons || []).filter(reason =>
    ['NO_LAB_ACCESS', 'NO_BADGE_AUTH'].includes(reason.type) && unavailable.every(instance =>
      (instance.reasons || []).some(candidate => reasonKey(candidate) === reasonKey(reason)))) : [];
  const sharedKeys = new Set(shared.map(reasonKey));
  const availableCount = instances.length - unavailable.length;

  return <Box>
    <Text fontSize="sm" color="gray.600" mb={4}>
      <Box as="strong" color="gray.800">{labId}</Box> · {availableCount} of {instances.length} tool instances available
    </Text>
    {shared.length > 0 && <Flex align="flex-start" gap={3} bg="gray.50" borderWidth="1px"
      borderColor="gray.200" borderRadius="lg" p={4} mb={4}>
      <InfoIcon color="gray.600" flexShrink={0} mt={0.5} />
      <Box minW={0}>
        <Text fontSize="sm" fontWeight="semibold">Shared access requirements</Text>
        <Text fontSize="sm" color="gray.600" mt={1}>
          These apply to all {unavailable.length} tool instances marked “No access” below.
        </Text>
        <Box as="ul" pl={4} mt={2} fontSize="sm" color="gray.700">
          {shared.map(reason => <Box as="li" key={reasonKey(reason)}>{requirementMessage(reason, badgeNames)}</Box>)}
        </Box>
      </Box>
    </Flex>}
    <Box as="ul" listStyleType="none" m={0} p={0} borderWidth="1px" borderColor="gray.200" borderRadius="lg" overflow="hidden" bg="white">
      {orderedInstances.map((instance, index) => {
        const details = instance.canAccess ? [] : (instance.reasons || []).filter(reason => !sharedKeys.has(reasonKey(reason)));
        const StatusIcon = instance.canAccess ? CheckIcon : CloseIcon;
        return <Box as="li" key={instance.machineID} px={4} py={3}
          borderTopWidth={index ? '1px' : 0} borderColor="gray.200">
          <Flex align="flex-start" justify="space-between" gap={3} flexWrap="wrap">
            <Box minW={0} flex="1" flexBasis="180px">
              <Text fontSize="sm" fontWeight="semibold" overflowWrap="anywhere">{instance.machineName}</Text>
              {instance.machineTypeName && <Text fontSize="xs" color="gray.600" mt={1}>Tool: {instance.machineTypeName}</Text>}
            </Box>
            <Badge colorScheme={instance.canAccess ? 'green' : 'gray'} display="inline-flex" alignItems="center" gap={1} flexShrink={0}>
              <StatusIcon boxSize={3} />{instance.canAccess ? 'Can access' : 'No access'}
            </Badge>
          </Flex>
          {details.length > 0 && <Box as="ul" fontSize="sm" color="gray.600" mt={2} pl={4}>
            {details.map((reason, reasonIndex) => <Box as="li" key={reasonIndex} overflowWrap="anywhere">{requirementMessage(reason, badgeNames)}</Box>)}
          </Box>}
        </Box>;
      })}
    </Box>
  </Box>;
}
